import { Box, BoxProps, Divider, SimpleGrid, Skeleton } from '@chakra-ui/react'
import { Fragment, ReactNode } from 'react'

import { DetailField } from '../DetailField'

export type DetailSheetLayout = 'rows' | 'grid'

/**
 * Larguras da barra do esqueleto, irregulares de propósito e fixas por posição — com
 * `Math.random()` cada render sorteia outra e as barras mudam de tamanho enquanto a requisição
 * não volta. É a mesma decisão que o `VideoQuizViewModal` já documentava para as respostas.
 */
const SKELETON_WIDTHS = ['160px', '120px', '200px', '140px', '180px']

export interface IDetailSheetField {
  label: ReactNode
  /** O valor já formatado. Vazio vira o `emptyValue` do `DetailField` (`-` em `gray.400`). */
  value?: ReactNode
  /** Classe do LineIcons, para o campo que abre a ficha com o ladrilho de 32px. Só em `grid`. */
  icon?: string
  /** Texto do valor ausente, para a ficha de resposta de formulário (`Sem resposta`). */
  emptyValue?: ReactNode
  /** Quantas colunas o campo ocupa na grade. Só em `grid`. */
  colSpan?: number
}

export interface IDetailSheetProps extends Omit<BoxProps, 'title'> {
  /**
   * Os campos da ficha. Entradas falsas são descartadas, então um campo condicional é
   * `cond && { label, value }` — e não um `...(cond ? [x] : [])` no meio do array.
   */
  fields: (IDetailSheetField | false | null | undefined | '')[]
  /**
   * Forma da ficha, não medida do Chakra — a mesma ideia do `size` do `FormModal`.
   * `rows` (padrão) é a lista de pares rótulo/valor com o valor à direita, separados por um
   * filete: a ficha de um comprovante, de uma entrega, de uma solicitação. `grid` é a grade de
   * pares empilhados, para a ficha que se lê em blocos (o aproveitamento de estudos, a inscrição
   * num concurso).
   */
  layout?: DetailSheetLayout
  /** Colunas da grade. Só em `grid`; o padrão acompanha a largura do modal. */
  columns?: number | Record<string, number>
  /** Enquanto os dados vêm, cada valor vira uma barra de esqueleto — o rótulo já é conhecido. */
  isLoading?: boolean
}

/**
 * A moldura da ficha de detalhe — o quadro que faltava ao lado do `<DetailField>`, que já
 * resolvia o par rótulo/valor.
 *
 * Eram **seis arranjos em 16 telas**: o `<Table>` usado como ficha (5 arquivos), `SimpleGrid` +
 * `DetailField` (`StudyCreditViewModal`), `VStack` + `Divider` (`SalesReceiptViewModal`), um
 * `Grid` com o par escrito à mão em dois `<Text>` (`ContestApplicationModal`), o `FormField`
 * como rótulo de leitura (`UtmGeneratorViewModal`) e o `<strong>Rótulo:</strong>` dentro de um
 * `<Text>` (`JobApplicationViewModal`). `EntregaDeTrabalhoViewModal` e `VideoQuizViewModal` eram
 * **quase byte a byte iguais** — a mesma tabela, o mesmo `<Skeleton isLoaded>` linha a linha —, e
 * o `DetailField` só era importado em **2 dos 7** `*ViewModal`.
 *
 * **A ficha não é uma tabela, e por isso não leva `<TableContainer>`.** Um `<Table>` é uma
 * listagem de registros; uma ficha é **um** registro, que é o que um `<dl>` quer dizer — e é o
 * que o `DetailField` emite. O `AGENTS.md` já dizia que essas telas ficavam sem o contêiner
 * (o `white-space: nowrap` dele transforma uma resposta longa em rolagem horizontal); aqui a
 * pergunta deixa de existir, porque não há tabela nenhuma.
 *
 * **O esqueleto é do componente.** As duas telas gêmeas repetiam `<Skeleton isLoaded={!loading}>`
 * em cada linha com `{value || '-'}` dentro, e esse `'-'` não era um valor vazio: era a **medida**
 * da barra, que sem ele colapsava. Aqui a barra tem largura própria e o `'-'` volta a ser o que
 * sempre foi — o valor que a API devolveu vazio.
 */
export default function DetailSheet({
  fields,
  layout = 'rows',
  columns = { base: 1, md: 2 },
  isLoading,
  ...rest
}: IDetailSheetProps) {
  const visible = fields.filter((field): field is IDetailSheetField => Boolean(field))

  const valueOf = (field: IDetailSheetField, index: number) =>
    isLoading ? (
      <Skeleton
        height="14px"
        w={SKELETON_WIDTHS[index % SKELETON_WIDTHS.length]}
        maxW="100%"
        ml={layout === 'rows' ? 'auto' : 0}
      />
    ) : (
      field.value
    )

  if (layout === 'grid') {
    return (
      <SimpleGrid columns={columns} spacing={4} {...rest}>
        {visible.map((field, index) => (
          <DetailField
            key={index}
            label={field.label}
            value={valueOf(field, index)}
            icon={field.icon}
            emptyValue={field.emptyValue}
            gridColumn={field.colSpan ? `span ${field.colSpan}` : undefined}
          />
        ))}
      </SimpleGrid>
    )
  }

  return (
    <Box {...rest}>
      {visible.map((field, index) => (
        <Fragment key={index}>
          {index > 0 && <Divider />}
          <DetailField
            layout="inline"
            py={2}
            label={field.label}
            value={valueOf(field, index)}
            emptyValue={field.emptyValue}
          />
        </Fragment>
      ))}
    </Box>
  )
}
